<template>
  <v-container class="py-md-8" style="max-width: 750px">
    <h2 class="text-h2 flex-fill px-3 mb-6">Admins</h2>
    <v-data-table
      :headers="headers"
      :items="items"
      item-key="name"
      class="elevation-2"
      :search="search"
      :custom-filter="filter"
      :loading="loading"
    >
      <template v-slot:top>
        <div class="d-flex align-center pa-4">
          <v-text-field
            rounded
            flat
            filled
            type="search"
            v-model="search"
            dense
            hide-details=""
            placeholder="Search Users"
            class="mr-2 flex-fill"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                fab
                color="primary"
                small
                elevation="0"
                @click="edit(null)"
                ><font-awesome-icon icon="plus"></font-awesome-icon
              ></v-btn>
            </template>
            <span>Add User</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              @click="edit(item)"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon @click="confirmDelete(item)">
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="openEditor" max-width="600px" :persistent="loading">
      <ValidationObserver
        ref="observer"
        tag="form"
        @submit.prevent="onSubmit"
        v-slot="{ pristine }"
      >
        <v-card>
          <dialog-title
            v-model="openEditor"
            :title="`${item.isNew ? 'Add' : 'Edit'} User`"
            :disabled="loading"
          ></dialog-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                Fill out the form below to add a new <strong>admin</strong>.
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  rules="required|email|max:255"
                  v-slot="{ errors }"
                  vid="Id"
                >
                  <v-text-field
                    v-model="item.id"
                    label="Email*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" sm="6">
                <ValidationProvider
                  rules="required|min:2|max:255"
                  v-slot="{ errors }"
                  vid="FirstName"
                >
                  <v-text-field
                    v-model="item.firstName"
                    label="First Name*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" sm="6">
                <ValidationProvider
                  rules="required|min:2|max:255"
                  v-slot="{ errors }"
                  vid="LastName"
                >
                  <v-text-field
                    v-model="item.lastName"
                    label="Last name*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" v-if="item.isNew">
                <ValidationProvider
                  vid="Password"
                  rules="required|max:255"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="item.password"
                    label="Password*"
                    type="password"
                    outlined
                    :error-messages="getPasswordErrors(pristine, errors)"
                    hide-details
                    :disabled="loading"
                  ></v-text-field>
                  <ul class="mt-2">
                    <li
                      class="text-caption font-weight-bold"
                      :class="{
                        'success--text': passwordHasLength,
                        'red--text': !pristine && !passwordHasLength,
                      }"
                    >
                      Must be at least 6 characters long
                    </li>
                    <li
                      class="text-caption font-weight-bold"
                      :class="{
                        'success--text': passwordHasLower,
                        'red--text': !pristine && !passwordHasLower,
                      }"
                    >
                      Must contain a lowercase letter.
                    </li>
                    <li
                      class="text-caption font-weight-bold"
                      :class="{
                        'success--text': passwordHasUpper,
                        'red--text': !pristine && !passwordHasUpper,
                      }"
                    >
                      Must contain a uppercase letter.
                    </li>
                    <li
                      class="text-caption font-weight-bold"
                      :class="{
                        'success--text': passwordHasNumber,
                        'red--text': !pristine && !passwordHasNumber,
                      }"
                    >
                      Must contain a number.
                    </li>
                  </ul>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" v-if="item.isNew">
                <ValidationProvider
                  rules="required|confirmed:Password"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="item.confirmPassword"
                    name="password confirmation"
                    label="Confirm Password*"
                    type="password"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn
                  :disabled="loading"
                  text
                  color="primary"
                  @click="openEditor = false"
                >
                  Cancel
                </v-btn>
                <v-btn :loading="loading" type="submit" color="primary">
                  Save
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog v-model="openConfirmDelete" max-width="400px">
      <v-card>
        <dialog-title
          v-model="openConfirmDelete"
          title="Confirm Delete"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete user:
                <strong class="red--text darken-1 font-weight-bold">{{
                  item.id
                }}</strong
                >. <br /><br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirmDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="deleteUser"
              >
                Yes, delete user
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import DialogTitle from "../../components/DialogTitle.vue";
import { roles } from "../../constants/Identity";

const newUser = () => ({
  id: "",
  password: "",
  firstName: "",
  lastName: "",
  isNew: true,
});

export default {
  components: { DialogTitle },
  data() {
    return {
      search: "",
      items: [],
      item: newUser(),
      openEditor: false,
      openConfirmDelete: false,
      pendingStateChanges: 0,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: "Email",
          value: "id",
        },
        {
          text: "First Name",
          value: "firstName",
        },
        {
          text: "Last Name",
          value: "lastName",
        },
        // {
        //   text: "Role",
        //   value: "role",
        // },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          cellClass: "w-0 text-no-wrap",
          align: "center",
        },
      ];
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    passwordHasLength() {
      return this.item.password.length >= 6;
    },
    passwordHasUpper() {
      return /[A-Z]/.test(this.item.password);
    },
    passwordHasLower() {
      return /[a-z]/.test(this.item.password);
    },
    passwordHasNumber() {
      return /[0-9]/.test(this.item.password);
    },
    validPassword() {
      return (
        !this.item.isNew ||
        (this.passwordHasLength &&
          this.passwordHasUpper &&
          this.passwordHasNumber &&
          this.passwordHasNumber)
      );
    },
  },
  methods: {
    filter(value, search) {
      return (
        value != null &&
        search != null &&
        typeof value === "string" &&
        value.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
      );
    },
    load() {
      this.pendingStateChanges++;

      return axios
        .get("/api/users")
        .then(
          (result) => {
            this.items = result;
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading users. Please try again later."
            );
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to load users. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    edit(item) {
      this.item = item ? JSON.parse(JSON.stringify(item)) : newUser();
      if (!this.item.password) this.item.password = "";
      if (this.$refs.observer) this.$refs.observer.reset();
      this.openEditor = true;
    },
    confirmDelete(item) {
      this.item = item;
      this.openConfirmDelete = true;
    },
    deleteUser() {
      this.pendingStateChanges++;

      axios
        .post(`api/users/delete`, {
          id: this.item.id,
        })
        .then(
          () => {
            this.openConfirmDelete = false;
            let index = this.items.findIndex((x) => x.id === this.item.id);
            this.items.splice(index, 1);
            this.load();
            this.$store.dispatch(
              "notifications/success",
              "Successfully deleted items."
            );
          },
          (error) => {
            console.warn(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to delete items. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    async onSubmit() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid || !this.validPassword || this.loading) return;

      this.pendingStateChanges++;

      axios
        .post(`api/users/${this.item.isNew ? "create" : ""}`, {
          id: this.item.id,
          firstName: this.item.firstName,
          lastName: this.item.lastName,
          password: this.item.password,
          role: roles.admin,
        })
        .then(
          () => {
            this.openEditor = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully ${this.item.isNew ? "added" : "updated"} items.`
            );
            this.load();
          },
          (errorResponse) => {
            if (errorResponse.statusCode === 400) {
              this.$refs.observer.setErrors(errorResponse.result);
            } else {
              this.$store.dispatch(
                "notifications/error",
                "An unexpected error occurred while trying to login. Please try again later."
              );
            }
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
        });
    },
    getPasswordErrors(pristine, fieldErrors) {
      let errors = [];
      if (!pristine) {
        if (!this.validPassword) errors.push("invalid password");
        errors = errors.concat(fieldErrors);
      }

      return errors;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style>
.w-0 {
  width: 0;
}
</style>
